/**
 * LOCO — core storefront behavior.
 * Header state, money formatting, toast, section fetching, recommendations.
 */
(() => {
  const LOCO = (window.LOCO = window.LOCO || {});

  /* ---------- Money (mirrors Shopify's money_format placeholders) ---------- */
  LOCO.formatMoney = function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || LOCO.moneyFormat || '${{amount}}';
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return String(cents);
    const toParts = (value, precision, thousands, decimal) => {
      const number = (Number(value) / 100).toFixed(precision);
      const [whole, dec] = number.split('.');
      const withThousands = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return dec ? withThousands + decimal + dec : withThousands;
    };
    const map = {
      amount: () => toParts(cents, 2, ',', '.'),
      amount_no_decimals: () => toParts(cents, 0, ',', '.'),
      amount_with_comma_separator: () => toParts(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => toParts(cents, 0, '.', ','),
      amount_with_apostrophe_separator: () => toParts(cents, 2, "'", '.'),
      amount_with_space_separator: () => toParts(cents, 2, ' ', ','),
      amount_no_decimals_with_space_separator: () => toParts(cents, 0, ' ', ','),
      amount_with_period_and_space_separator: () => toParts(cents, 2, ' ', '.')
    };
    const value = (map[match[1]] || map.amount)();
    return fmt.replace(match[0], value);
  };

  /* ---------- Toast ---------- */
  let toastTimer;
  LOCO.toast = function toast(message, type = 'info') {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('toast--error', type === 'error');
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  };

  /* ---------- Section rendering helper ---------- */
  LOCO.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  LOCO.fetchSection = async function fetchSection(url, { signal } = {}) {
    const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
    return LOCO.parseHTML(await response.text());
  };

  /* ---------- Header: measured height, sticky offset, transparent state ---------- */
  function initHeader() {
    const section = document.querySelector('[data-header-section]');
    if (!section) return;
    const header = section.querySelector('.header');
    const root = document.documentElement;

    const measure = () => {
      const height = header.getBoundingClientRect().height;
      root.style.setProperty('--header-height', `${Math.round(height)}px`);
      const sticky = section.classList.contains('is-sticky');
      root.style.setProperty('--sticky-offset', sticky ? `${Math.round(height)}px` : '0px');
    };
    measure();
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header);

    if (section.classList.contains('is-transparent')) {
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;top:0;height:80px;width:1px;pointer-events:none;';
      document.body.prepend(sentinel);
      new IntersectionObserver(([entry]) => {
        section.classList.toggle('is-scrolled', !entry.isIntersecting);
      }).observe(sentinel);
    }
  }

  /* ---------- Product recommendations (Shopify native) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = () => this.load();
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            load();
          }
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      io.observe(this);
    }

    async load() {
      try {
        const doc = await LOCO.fetchSection(this.dataset.url);
        const fresh = doc.querySelector(`product-recommendations[data-section-id="${this.dataset.sectionId}"]`) || doc.querySelector('product-recommendations');
        if (fresh && fresh.querySelector('[data-rec-item]')) {
          this.innerHTML = fresh.innerHTML;
          this.classList.add('is-loaded');
          this.dispatchEvent(new CustomEvent('loco:content-loaded', { bubbles: true }));
        } else {
          this.closest('.shopify-section')?.setAttribute('hidden', '');
        }
      } catch (error) {
        // Recommendations are optional: hide quietly
        this.closest('.shopify-section')?.setAttribute('hidden', '');
      }
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Share button (native share with copy fallback) ---------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-share]');
    if (!button) return;
    const url = button.dataset.shareUrl || window.location.href;
    const title = button.dataset.shareTitle || document.title;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        LOCO.toast(button.dataset.copiedText || 'Link copied');
      }
    } catch (error) {
      /* user cancelled share sheet */
    }
  });

  /* ---------- Collection filters: submit on change, keep URL shareable ---------- */
  document.addEventListener('change', (event) => {
    const form = event.target.closest('form[data-auto-submit]');
    if (!form) return;
    if (event.target.matches('input[type="number"]')) return; // price inputs submit on Apply
    form.requestSubmit ? form.requestSubmit() : form.submit();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initHeader);
  else initHeader();

  // Theme Editor: re-measure when the header section is re-rendered
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-header-section]')) initHeader();
  });
})();
